import React from "react";
import { Check, ChevronLeft, ChevronRight, Link2, MousePointerClick, Trophy, User, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Field from "../Field";
import BannerUploader from "../../BannerUploader";
import RichTextEditor from "@/components/ui/rich-text-editor";

interface EventForm {
  name: string;
  date: string;
  location: string;
  fee: string;
  information: string;
  registrationLink: string;
  gameType: string;
  registrationType: string;
  isRated: boolean;
}

interface CreateEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventStep: number;
  setEventStep: (step: number) => void;
  eventForm: EventForm;
  setEventForm: React.Dispatch<React.SetStateAction<EventForm>>;
  eventBanner: string;
  setEventBanner: (url: string) => void;
  setEventBannerMediaId: (id: any) => void;
  loading: boolean;
  onNext: () => void;
  onCreate: () => void;
}

const CreateEventDialog: React.FC<CreateEventDialogProps> = ({
  open,
  onOpenChange,
  eventStep,
  setEventStep,
  eventForm,
  setEventForm,
  eventBanner,
  setEventBanner,
  setEventBannerMediaId,
  loading,
  onNext,
  onCreate,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Yeni tədbir yarat</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          {[1, 2].map((s) => (
            <React.Fragment key={s}>
              <div
                className={cn(
                  "flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all",
                  eventStep === s
                    ? "bg-blue-500 text-white"
                    : s < eventStep
                    ? "bg-green-500 text-white"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
                )}
              >
                {s < eventStep ? <Check className="w-3.5 h-3.5" /> : s}
              </div>
              {s < 2 && (
                <div className={cn("flex-1 h-0.5 rounded-full transition-all", s < eventStep ? "bg-green-400" : "bg-gray-100 dark:bg-gray-800")} />
              )}
            </React.Fragment>
          ))}
        </div>

        {eventStep === 1 && (
          <div className="space-y-3 max-h-[65vh] overflow-y-auto p-1 -m-1">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Əsas məlumatlar</p>
            <Field label="Tədbirin adı *">
              <Input
                placeholder="Məs: Quiz Night #1"
                value={eventForm.name}
                onChange={(e) => setEventForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field label="Tarix *">
              <input
                type="datetime-local"
                value={eventForm.date}
                onChange={(e) => setEventForm((f) => ({ ...f, date: e.target.value }))}
                className="profile-datetime-input w-full h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all"
              />
            </Field>
            <Field label="Ünvan *">
              <Input
                placeholder="Məs: Nizami küç. 12 və ya Online"
                value={eventForm.location}
                onChange={(e) => setEventForm((f) => ({ ...f, location: e.target.value }))}
              />
            </Field>
            <Field label="İştirak haqqı (AZN)">
              <Input
                type="number"
                placeholder="Məs: 10"
                value={eventForm.fee}
                onChange={(e) => setEventForm((f) => ({ ...f, fee: e.target.value }))}
              />
            </Field>
            <Field label="Əlavə məlumat">
              <RichTextEditor
                value={eventForm.information}
                onChange={(html) => setEventForm((f) => ({ ...f, information: html }))}
                placeholder="Tədbir haqqında ətraflı məlumat"
              />
            </Field>
            <Field label="Banner şəkli">
              <BannerUploader
                imageUrl={eventBanner}
                imageUpload={(url, mediaId) => {
                  setEventBanner(url);
                  setEventBannerMediaId(mediaId);
                }}
              />
            </Field>
            <Button onClick={onNext} className="w-full gap-2">
              İrəli
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {eventStep === 2 && (
          <div className="space-y-5 max-h-[65vh] overflow-y-auto p-1 -m-1">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Oyun və qeydiyyat parametrləri</p>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 block">Oyun növü *</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "team", icon: <Users className="w-6 h-6" />, label: "Komanda", desc: "Komandalar yarışır" },
                  { value: "individual", icon: <User className="w-6 h-6" />, label: "Fərdi", desc: "Fərdlər yarışır" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEventForm((f) => ({ ...f, gameType: opt.value }))}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all text-center",
                      eventForm.gameType === opt.value
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300"
                    )}
                  >
                    {opt.icon}
                    <span className="text-sm font-bold">{opt.label}</span>
                    <span className="text-xs opacity-70">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 block">Qeydiyyat növü *</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "button", icon: <MousePointerClick className="w-6 h-6" />, label: "Düymə ilə", desc: "İstifadəçilər birbaşa qeydiyyatdan keçir" },
                  { value: "link", icon: <Link2 className="w-6 h-6" />, label: "Link ilə", desc: "Xarici link vasitəsilə qeydiyyat" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEventForm((f) => ({ ...f, registrationType: opt.value }))}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all text-center",
                      eventForm.registrationType === opt.value
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300"
                    )}
                  >
                    {opt.icon}
                    <span className="text-sm font-bold">{opt.label}</span>
                    <span className="text-xs opacity-70">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {eventForm.registrationType === "link" && (
              <Field label="Qeydiyyat linki *">
                <Input
                  placeholder="Məs: https://forms.gle/..."
                  value={eventForm.registrationLink}
                  onChange={(e) => setEventForm((f) => ({ ...f, registrationLink: e.target.value }))}
                />
              </Field>
            )}

            <button
              type="button"
              onClick={() => setEventForm((f) => ({ ...f, isRated: !f.isRated }))}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-sm",
                eventForm.isRated
                  ? "border-amber-400 bg-amber-50 text-amber-700 font-semibold"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 dark:text-gray-400"
              )}
            >
              <Trophy className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">Reytinqli tədbir</span>
              <div className={cn(
                "w-10 h-5 rounded-full transition-all flex items-center",
                eventForm.isRated ? "bg-amber-400 justify-end" : "bg-gray-200 justify-start"
              )}>
                <div className="w-4 h-4 bg-white dark:bg-gray-900 rounded-full mx-0.5 shadow-sm" />
              </div>
            </button>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setEventStep(1)} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Geri
              </Button>
              <Button onClick={onCreate} disabled={loading} className="flex-1">
                Təsdiq üçün göndər
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateEventDialog;
